import { Injectable } from '@angular/core';
import { CallType, ErrorType, LiveChatEvent } from './care-analytics.enum';
import { SessionService } from './session.service';

export const ANALYTICS = {
  STRAPIUID: undefined,
};

/* eslint-disable no-unused-vars */
declare global {
  interface Window {
    dataLayer: any[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class CareAnalyticsService {
  // public strapiUID?: string = null;
  public pageIndex?: number = null;
  public pageType?: string = null;

  constructor(private sessionService: SessionService) {}

  /**
   * @extended analyticsService
   * Extends the analyticsService's to fix live bugs, to add the pageID and handle event tracking.
   * @param currentAppArea
   * @param pageType
   * @param level
   * @param customData
   * @param pageId
   */
  public trackPageView(currentAppArea?: string, level: number = 1, selectedDiagnosticActionCode = null) {    
    this.sessionService.getSessionData(undefined, false).subscribe((ses) => {
      if (
        this.pageIndex ||
        !sessionStorage?.getItem('sessionPreviousUrl') ||
        window?.location?.href !== sessionStorage?.getItem('sessionPreviousUrl')
      ) {
        const customData = this.getCustomData(ses, selectedDiagnosticActionCode);
        const pageViewData = this.getPageViewData(currentAppArea, level);
        const trackingData = {
          ...pageViewData,
          ...customData,
        };
        this.addDataLayer('pageview', trackingData);
      }
      sessionStorage.setItem('sessionPreviousUrl', window.location.href);
      ANALYTICS.STRAPIUID = undefined;
      this.pageIndex = null;
      this.sessionService.sessionDataCache = null;
    });
  }

  /**
   * Public method to add the dataLayer
   * @param event - the event name you would like to add
   * @param dataLayerObject - the new properties you would like to add
   */
  addDataLayer(event: string, dataLayerObject: any): void {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    window.dataLayer.push({
      ...dataLayerObject,
      event,
    });
  }

  /**
   * Used to update the dataLayer with a new event that does not yet exist in the current dataLayer
   * @param event - the event type e.g. content_actions
   * @param eventName - the event name  e.g. content_clicked
   * @param eventParams - the new properties you would like to add e.g. 
   * "event_params": {
      "content_type" : "web - faults - alternative support options",
	    "content_link": "https://www.virginmedia.com/", // replace this URL with the actual URL without query parameters
	    "content_name": "Fault appointment" // replace the value based on which alternative option is clicked on, 
      could be 'Help Hub', 'Change package', 'Alternative Dispute Resolution', etc.
     }
   */
  addContentEventToDataLayer(event = 'content_actions', eventName = 'content_clicked', eventParams: object): void {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    window.dataLayer.push({
      event,
      event_name: eventName,
      event_params: eventParams,
    });
  }

  public getCustomData(ses, selectedDiagnosticActionCode) {
    const customData: any = {};
    if (ses?.angularPublicSession?.householdId || ses?.angularPublicSession?.tid) {
      customData.user = this.getUserData(
        ses?.data?.sessionId,
        ses?.angularPublicSession?.householdId,
        ses?.angularPublicSession?.softIdentMethodValue,
        ses?.angularPublicSession?.serviceStatusResponse?.customerAccountAndCollectionStatus,
        ses?.angularPublicSession?.tid
      );
    }
    if (
      ses?.angularPublicSession?.serviceStatusResponse ||
      ses?.angularPublicSession?.faultAppointments ||
      ses?.angularPublicSession?.diagnosticId ||
      ses?.angularPublicSession?.diagnosticsActionCode
    ) {
      const numOfOutages =
        (ses?.angularPublicSession?.serviceStatusResponse?.currentOutagesByProductType?.BROADBAND?.outages?.length ||
          0) +
        (ses?.angularPublicSession?.serviceStatusResponse?.currentOutagesByProductType?.PHONE?.outages.length || 0) +
        (ses?.angularPublicSession?.serviceStatusResponse?.currentOutagesByProductType?.TV?.outages.length || 0);

      // @ts-ignore
      const einsteinActionCode1 = selectedDiagnosticActionCode || ses?.angularPublicSession?.einsteinActionCode1;

      customData.faults = this.getFaultsData(
        ses?.angularPublicSession?.serviceStatusResponse?.customerAccountAndCollectionStatus ||
          ses?.angularPublicSession?.accountStatus,
        ses?.angularPublicSession?.serviceStatusResponse ? (numOfOutages ? 'TRUE' : 'FALSE') : undefined,
        ses?.angularPublicSession?.faultAppointments || ses?.angularPublicSession?.description,
        ses?.angularPublicSession?.diagnosticId || ses?.angularPublicSession?.diagnosticIdentifier,
        ses?.angularPublicSession?.diagnosticsActionCode || einsteinActionCode1,
        einsteinActionCode1 ? ses?.angularPublicSession?.einsteinActionCode2 : null
      );
    }
    return customData;
  }

  /**
   * Duplicate of the private method getPageData to correct PageType / PageID bug.
   * @param currentAppArea
   * @param pageId
   * @param level
   * @returns
   */
  public getPageViewData(currentAppArea?: string, level: number = 1) {
    const cleanHostname = location?.hostname?.replace('www.', '');
    const subDomainRegex = /^([\w-]+)(?:\.[\w-]+){2,}$/;
    const subdomain = subDomainRegex.test(cleanHostname) ? cleanHostname.match(subDomainRegex)[1] : '';

    if (
      !this.pageIndex &&
      (window?.location?.pathname?.toLocaleLowerCase()?.endsWith('/advice') ||
        window?.location?.pathname?.toLocaleLowerCase()?.endsWith('/advices'))
    ) {
      this.pageIndex = 1;
    }

    return {
      page: {
        currentTimestamp: Date.now(),
        embeddedWebApp: 'false',
        environment:
          window?.location?.href?.indexOf('virginmedia.com') !== -1 && (subdomain === 'www' || subdomain === '')
            ? 'prod'
            : 'dev',
        pageReferrer: window?.document?.referrer,
        pageUrl: window?.location?.href,
        pageID: ANALYTICS.STRAPIUID,
        pageType: this.pageType || `${currentAppArea}${level ? ' | level' + level : ''}`,
        path: window?.location?.pathname + (this.pageIndex ? `-${this.pageIndex}` : ''),
        platform: 'AngularWeb',
        query: window?.location?.search,
        sessionReferrer: sessionStorage?.getItem('sessionReferrerUrl'),
        subdomain,
        pagePath: currentAppArea,
        pageModDate: null,
        journey: currentAppArea,
        pink: 'Non Pink',
      },
    };
  }

  public getUserData(
    conversationId?: string,
    softcableCustomerID?: string,
    softIdentMethod?: string,
    accountStatus?: string,
    tid?: string
  ) {
    return {
      conversationId,
      lastVisit: 'undefined',
      hashedEmail: 'undefined',
      cable: {
        softcableCustomerID,
        softIdentMethod,
        curHolding: 'undefined',
        curEOC: 'undefined',
        existingOffers: 'undefined',
        recommendations: 'undefined',
        accountStatus,
        tid,
      },
    };
  }

  public getFaultsData(
    accountStatus?: string,
    areaFault?: string,
    description?: string,
    diagnosticIdentifier?: string,
    einsteinActionCode1?: string,
    einsteinActionCode2?: string
  ) {
    return {
      accountStatus: accountStatus?.toUpperCase(),
      areaFault,
      description,
      diagnosticIdentifier,
      einsteinActionCode1,
      einsteinActionCode2,
    };
  }

  public trackPriorityCallEvent(priorityCallbackId: string): void {
    if (priorityCallbackId) {
      this.addDataLayer('content_actions', {
        event: 'content_actions',
        /* eslint-disable */
        event_name: 'request_call',
        eventCategory: 'request call',
        eventAction: 'priority call',
        eventLabel: priorityCallbackId, // dynamic uuid
        event_params: {
          priority_callback_id: priorityCallbackId, // dynamic uuid
        },
        /* eslint-enable */
      });
    }
  }

  public trackSiteErrorEvent(errorType: ErrorType, errorCode?: number, errorMessage?: string): void {
    this.addDataLayer('site_error', {
      event: 'site_error',
      /* eslint-disable */
      event_name: 'site_error',
      event_params: {
        error_type: errorType,
        error_code: errorCode,
        error_message: errorMessage,
        /* eslint-enable */
      },
    });
  }

  public trackCustomerFeedbackEvent(contentText?: string): void {
    if (contentText) {
      const feedbackTrigger = [
        "Yes, I'm back online",
        'The TV box is working again',
        'No, I still need help',
        'Yes',
        'No',
      ];
      contentText = contentText?.replace('’', "'")?.trim();
      if (feedbackTrigger.indexOf(contentText) > -1) {
        this.addDataLayer('feedback', {
          event: 'feedback',
          /* eslint-disable */
          event_name: 'online_support_feedback',
          event_params: {
            support_category: 'faults',
            content_text: contentText,
            content_type: `web - faults - csat ${contentText?.toLocaleLowerCase()}`,
            /* eslint-enable */
          },
        });
      }
    }
  }

  public trackBookATechnicianSlotEvent(
    engineerBookingId?: string,
    installationDate?: string,
    installationSlot?: string,
    bookingType?: string
  ): void {
    this.addDataLayer('ecommerce', null);
    this.addDataLayer('book_faults_engineer', {
      event: 'book_faults_engineer',
      /* eslint-disable */
      event_name: bookingType == 'cancellation' ? 'cancel_engineer_slot' : 'book_engineer_slot',
      event_params: {
        engineer_booking_id: engineerBookingId,
        installation_type: 'faults',
        installation_date: installationDate,
        installation_slot: installationSlot,
        booking_type: bookingType, // 'cancellation' | 'new booking' | 'amend booking'
        /* eslint-enable */
      },
    });
  }

  public trackBroadbandFaultCheckEvent(session?: any): void {
    const numOfOutages =
      (session?.angularPublicSession?.serviceStatusResponse?.currentOutagesByProductType?.BROADBAND?.outages?.length ||
        0) +
      (session?.angularPublicSession?.serviceStatusResponse?.currentOutagesByProductType?.PHONE?.outages.length || 0) +
      (session?.angularPublicSession?.serviceStatusResponse?.currentOutagesByProductType?.TV?.outages.length || 0);
    this.addDataLayer('faults_check', {
      event: 'faults_check',
      /* eslint-disable */
      event_name: 'faults_check',
      event_params: {
        account_status: session?.angularPublicSession?.serviceStatusResponse?.customerAccountAndCollectionStatus,
        area_fault: session?.angularPublicSession?.serviceStatusResponse ? (numOfOutages ? 'TRUE' : 'FALSE') : null,
        description: session?.angularPublicSession?.faultAppointments,
        diagnostic_identifier: session?.angularPublicSession?.diagnosticId,
        einstein_action_code_1: session?.angularPublicSession?.diagnosticsActionCode,
        einstein_action_code_2: null,
        access_point_data_exists: session?.angularPublicSession?.accessPointActionCodes
          ? session?.angularPublicSession?.accessPointActionCodes?.length
            ? 'TRUE'
            : 'FALSE'
          : null,
        access_point_error_count: session?.angularPublicSession?.accessPointActionCodes?.length,
        client_device_data_exist: session?.angularPublicSession?.clientDevices
          ? session?.angularPublicSession?.clientDevices?.length
            ? 'TRUE'
            : 'FALSE'
          : null,
        client_device_count: session?.angularPublicSession?.clientDevices,
        client_device_error_count: session?.angularPublicSession?.totalNumberOfDevices,
        wo_order_number: null,
        postcode: this.getCleanPostcode(session?.angularPublicSession?.postcode),
        premise_uid: session?.angularPublicSession?.householdId,
        /* eslint-enable */
      },
    });
  }

  public trackTVFaultCheckEvent(session: any): void {
    const numOfOutages =
      (session?.angularPublicSession?.serviceStatusResponse?.currentOutagesByProductType?.BROADBAND?.outages?.length ||
        0) +
      (session?.angularPublicSession?.serviceStatusResponse?.currentOutagesByProductType?.PHONE?.outages.length || 0) +
      (session?.angularPublicSession?.serviceStatusResponse?.currentOutagesByProductType?.TV?.outages.length || 0);
    this.addDataLayer('faults_check', {
      event: 'faults_check',
      /* eslint-disable */
      event_name: 'faults_check',
      event_params: {
        account_status: session?.angularPublicSession?.accountStatus,
        area_fault: session?.angularPublicSession?.serviceStatusResponse ? (numOfOutages ? 'TRUE' : 'FALSE') : null,
        description: session?.angularPublicSession?.description,
        diagnostic_identifier: session?.angularPublicSession?.diagnosticIdentifier,
        einstein_action_code_1: session?.angularPublicSession?.einsteinActionCode1,
        einstein_action_code_2: session?.angularPublicSession?.einsteinActionCode2,
        access_point_data_exists: session?.angularPublicSession?.accessPointActionCodes
          ? session?.angularPublicSession?.accessPointActionCodes?.length
            ? 'TRUE'
            : 'FALSE'
          : null,
        access_point_error_count: session?.angularPublicSession?.accessPointActionCodes?.length,
        client_device_data_exist: session?.angularPublicSession?.clientDevices
          ? session?.angularPublicSession?.clientDevices?.length
            ? 'TRUE'
            : 'FALSE'
          : null,
        client_device_count: session?.angularPublicSession?.clientDevices,
        client_device_error_count: session?.angularPublicSession?.totalNumberOfDevices,
        wo_order_number: null,
        postcode: this.getCleanPostcode(session?.angularPublicSession?.postcode),
        premise_uid: session?.angularPublicSession?.householdId,
        /* eslint-enable */
      },
    });
  }

  // eslint-disable-next-line
  public trackLiveChatEvent(liveChatEvent: LiveChatEvent, livePersonId?: string): void {
    // INFO: ZEUS-6659 - Disabled Live Person tracking for now.
    /*
    if (livePersonId) {
      this.addDataLayer('live_person', {
        event: 'live_person',
        eventAction: 'liveperson',
        eventCategory: 'site interaction',
        eventLabel: 'live-chat:opened',
        event_name: liveChatEvent,
        event_params: {
          live_person_id: livePersonId,
        },
      });
    } else {
      this.addDataLayer('live_person', {
        event: 'live_person',
        eventAction: 'liveperson',
        eventCategory: 'site interaction',
        eventLabel: 'live-chat:opened',
        event_name: liveChatEvent,
      });
    }
    */
  }

  public trackCallModalViewEvent(callType: CallType): void {
    this.addDataLayer('call', {
      event: 'call',
      /* eslint-disable */
      event_name: 'call_support',
      event_params: {
        call_type: callType,
        /* eslint-enable */
      },
    });
  }

  public trackBroadbandCompensationEvent(
    postcode?: string,
    premiseUID?: string,
    outageID?: string,
    outageAlertSubscribed?: string
  ): void {
    this.addDataLayer('broadband_compensation', {
      event: 'broadband_compensation',
      /* eslint-disable */
      event_name: 'broadband_compensation_registration',
      event_params: {
        postcode: this.getCleanPostcode(postcode),
        premise_uid: premiseUID,
        outage_id: outageID,
        outage_alert_subscribed: outageAlertSubscribed,
        /* eslint-enable */
      },
    });
  }

  public getCleanPostcode(postcode: string): string {
    if (postcode) {
      const postcodeRegEx = /^[A-Z0-9]{2,4}(?=(?:\s*[A-Z0-9]{3})?$)/gm;
      postcode = postcodeRegEx.test(postcode) ? postcode.match(postcodeRegEx)[0] : '';
    }
    return postcode;
  }
}
